import React from 'react';
import './DeadCart.scss';
import {
    LineChart, Line, AreaChart, Area,
    XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { MdEmail, MdPhone, MdNotifications, MdChevronRight } from 'react-icons/md';

const DeadCart = () => {
    // Enhanced sample data
    const analyticsData = {
        totalDeadCarts: 156,
        recoveryRate: 45,
        averageValue: 819.99,
        monthlyTrend: [
            { date: '2024-01', abandonedCarts: 120, recoveredCarts: 45 },
            { date: '2024-02', abandonedCarts: 150, recoveredCarts: 60 },
            { date: '2024-03', abandonedCarts: 130, recoveredCarts: 55 },
            { date: '2024-04', abandonedCarts: 160, recoveredCarts: 75 },
            { date: '2024-05', abandonedCarts: 140, recoveredCarts: 65 },
            { date: '2024-06', abandonedCarts: 180, recoveredCarts: 90 },
        ]
    };

    const recoveryData = [
        { month: 'Jan', rate: 35 },
        { month: 'Feb', rate: 40 },
        { month: 'Mar', rate: 42 },
        { month: 'Apr', rate: 47 },
        { month: 'May', rate: 45 },
        { month: 'Jun', rate: 50 },
    ];

    return (
        <div className="dead-cart">
            <header className="dead-cart-header">
                <h1>Dead Cart Analytics</h1>
                <div className="dead-cart-period-selector">
                    <select defaultValue="30">
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                    </select>
                </div>
            </header>

            <div className="dead-cart-stats-grid">
                <div className="dead-cart-stat-card">
                    <h3>Total Dead Carts</h3>
                    <p className="dead-cart-stat-value">{analyticsData.totalDeadCarts}</p>
                    <span className="dead-cart-stat-label">This month</span>
                </div>
                <div className="dead-cart-stat-card">
                    <h3>Recovery Rate</h3>
                    <p className="dead-cart-stat-value">{analyticsData.recoveryRate}%</p>
                    <span className="dead-cart-stat-label">Average</span>
                </div>
                <div className="dead-cart-stat-card">
                    <h3>Average Cart Value</h3>
                    <p className="dead-cart-stat-value">₹{analyticsData.averageValue}</p>
                    <span className="dead-cart-stat-label">Per cart</span>
                </div>
                <div className="dead-cart-stat-card">
                    <h3>Lost Revenue</h3>
                    <p className="dead-cart-stat-value">₹ 14,038</p>
                    <span className="dead-cart-stat-label">Potential</span>
                </div>
            </div>

            <div className="dead-cart-charts-container">
                <div className="dead-cart-chart-box">
                    <h3>Abandonment Trend</h3>
                    <div className="dead-cart-chart">
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart
                                data={analyticsData.monthlyTrend}
                                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                            >
                                <defs>
                                    <linearGradient id="abandonedColor" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3498db" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#3498db" stopOpacity={0.1}/>
                                    </linearGradient>
                                    <linearGradient id="recoveredColor" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#2ecc71" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#2ecc71" stopOpacity={0.1}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                />
                                <Legend />
                                <Area
                                    type="monotone"
                                    dataKey="abandonedCarts"
                                    name="Abandoned Carts"
                                    stroke="#3498db"
                                    fillOpacity={1}
                                    fill="url(#abandonedColor)"
                                />
                                <Area
                                    type="monotone"
                                    dataKey="recoveredCarts"
                                    name="Recovered Carts"
                                    stroke="#2ecc71"
                                    fillOpacity={1}
                                    fill="url(#recoveredColor)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="dead-cart-chart-box">
                    <h3>Recovery Success Rate</h3>
                    <div className="dead-cart-chart">
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart
                                data={recoveryData}
                                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis 
                                    domain={[0, 100]}
                                    tickFormatter={(value) => `${value}%`}
                                />
                                <Tooltip
                                    formatter={(value) => [`${value}%`, 'Recovery Rate']}
                                    contentStyle={{ 
                                        backgroundColor: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="rate"
                                    stroke="#e74c3c"
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                    activeDot={{ r: 6 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="dead-cart-table-section">
                <h3>Recent Dead Carts</h3>
                <div className="dead-cart-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Cart Value</th>
                                <th>Items</th>
                                <th>Status</th>
                                <th>Action</th>
                                <th>Contact</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>2024-03-15</td>
                                <td>₹ 129.99</td>
                                <td>3</td>
                                <td>
                                    <span className="dead-cart-status pending">Pending</span>
                                </td>
                                <td>
                                    <button className="dead-cart-action-btn">Recover</button>
                                </td>
                                <td className="dead-cart-contact-icons">
                                    <button className="dead-cart-icon-btn email">
                                        <MdEmail />
                                    </button>
                                    <button className="dead-cart-icon-btn phone">
                                        <MdPhone />
                                    </button>
                                    <button className="dead-cart-icon-btn notification">
                                        <MdNotifications />
                                    </button>
                                </td>
                                <td>
                                    <button className="dead-cart-details-btn">
                                        <MdChevronRight />
                                    </button>
                                </td>
                            </tr>
                            <tr>
                                <td>2024-03-14</td>
                                <td>₹ 299.50</td>
                                <td>5</td>
                                <td>
                                    <span className="dead-cart-status processing">Processing</span>
                                </td>
                                <td>
                                    <button className="dead-cart-action-btn">Recover</button>
                                </td>
                                <td className="dead-cart-contact-icons">
                                    <button className="dead-cart-icon-btn email">
                                        <MdEmail />
                                    </button>
                                    <button className="dead-cart-icon-btn phone">
                                        <MdPhone />
                                    </button>
                                    <button className="dead-cart-icon-btn notification">
                                        <MdNotifications />
                                    </button>
                                </td>
                                <td>
                                    <button className="dead-cart-details-btn">
                                        <MdChevronRight />
                                    </button>
                                </td>
                            </tr>
                            <tr>
                                <td>2024-03-13</td>
                                <td>₹ 89.99</td>
                                <td>2</td>
                                <td>
                                    <span className="dead-cart-status failed">Failed</span>
                                </td>
                                <td>
                                    <button className="dead-cart-action-btn">Recover</button>
                                </td>
                                <td className="dead-cart-contact-icons">
                                    <button className="dead-cart-icon-btn email">
                                        <MdEmail />
                                    </button>
                                    <button className="dead-cart-icon-btn phone">
                                        <MdPhone />
                                    </button>
                                    <button className="dead-cart-icon-btn notification">
                                        <MdNotifications />
                                    </button>
                                </td>
                                <td>
                                    <button className="dead-cart-details-btn">
                                        <MdChevronRight />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default DeadCart;
